import pptxgen from 'pptxgenjs';
import type { Presentation } from './types';

/**
 * A standalone service to convert a Presentation object into a .pptx Blob.
 * Use this for background processing, S3 uploads, or other non-UI tasks.
 * 
 * This replicates the logic from PptxExporter but returns a Blob instead of
 * triggering a browser download.
 */
export class PptxBlobExporter {
    /**
     * Converts a Presentation object into a .pptx Blob.
     * @param presentation The presentation data to export.
     * @returns A Promise that resolves to a Blob containing the .pptx file.
     */
    async exportToBlob(presentation: Presentation): Promise<Blob> {
        const pptx = new pptxgen();

        const emuWidth = presentation.layout?.width || 12192000;
        const emuHeight = presentation.layout?.height || 6858000;
        const canvasWidth = 1200;
        const canvasHeight = 675;

        const getInchesX = (px: number) => (px / canvasWidth) * (emuWidth / 914400);
        const getInchesY = (px: number) => (px / canvasHeight) * (emuHeight / 914400);

        presentation.slides.forEach(slide => {
            const pptxSlide = pptx.addSlide();

            // Sort by zIndex for export layering
            const sortedElements = [...slide.elements].sort((a, b) => a.zIndex - b.zIndex);

            sortedElements.forEach(el => {
                const transparency = el.opacity !== undefined ? (1 - el.opacity) * 100 : 0;
                const common = {
                    x: getInchesX(el.x),
                    y: getInchesY(el.y),
                    w: getInchesX(el.width),
                    h: getInchesY(el.height),
                };

                if (el.type === 'text') {
                    pptxSlide.addText(el.content, {
                        ...common,
                        fontSize: (el.fontSize || 18) / 2, // Halve for export to match canvas scale
                        color: el.color?.replace('#', '') || '000000',
                        fontFace: el.fontFamily || 'Arial'
                    });
                } else if (el.type === 'image') {
                    pptxSlide.addImage({
                        ...common,
                        path: el.src,
                        transparency: transparency
                    });
                } else if (el.type === 'shape') {
                    // Map basic shapes to pptxgen types
                    const shapeType = el.shapeType === 'ellipse' ? pptx.ShapeType.ellipse : pptx.ShapeType.rect;
                    pptxSlide.addShape(shapeType, {
                        ...common,
                        fill: {
                            color: el.fill?.replace('#', '') || 'CCCCCC',
                            transparency: transparency > 0 ? Math.min(transparency + 10, 100) : 0
                        }
                    });
                }
            });
        });

        // Generate the blob using 'blob' output type
        const output = await pptx.write({ outputType: 'blob' });
        return output as Blob;
    }
}
